/**
 * Tipi del motore e-commerce visti dal browser (08 sez. 3.2, 16 sez. 3.2, migration B1 v6).
 * Solo tipi e costanti: nessun import React, nessun IO. Le righe arrivano da Supabase (letture con RLS)
 * o dalle rotte del core (azioni): nomi dei campi in snake_case come sul filo.
 */

export const COMMERCE_PROVIDERS = ['shopify', 'woocommerce'] as const;
export type CommerceProvider = (typeof COMMERCE_PROVIDERS)[number];

/** CHECK di commerce_stores.status (B1 v6, con suspended_billing di 11 A6). */
export const STORE_STATUSES = [
  'analyzing',
  'awaiting_confirmation',
  'syncing',
  'connected',
  'paused',
  'reauth_required',
  'suspended_billing',
  'disconnected',
] as const;
export type StoreStatus = (typeof STORE_STATUSES)[number];

/** CHECK di product_sync_jobs.status (fase 1: con paused e held). */
export const JOB_STATUSES = ['pending', 'running', 'completed', 'failed', 'cancelled', 'paused', 'held'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** CHECK di product_sync_jobs.job_kind. */
export const JOB_KINDS = [
  'connect_check', 'analysis', 'preview', 'initial_import', 'catalog_apply', 'stock_apply', 'reconcile',
  'reconcile_nightly', 'backfill_orders', 'snapshot', 'restore', 'undo_job', 'export',
] as const;
export type JobKind = (typeof JOB_KINDS)[number];

export const CONNECT_REQUEST_STATUSES = ['awaiting_app', 'ready', 'authorizing', 'completed', 'cancelled', 'expired'] as const;
export type ConnectRequestStatus = (typeof CONNECT_REQUEST_STATUSES)[number];

export const EVENT_KINDS = ['webhook', 'sync', 'settings_change', 'alert', 'preview', 'plugin_event'] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

export type PauseReason = 'manual' | 'store_down' | 'circuit' | 'billing' | 'global';

/** Riga di commerce_stores letta dal browser (mai segreti: stanno in commerce_store_secrets). */
export interface CommerceStoreRow {
  id: string;
  organization_id: string;
  provider: CommerceProvider;
  shop_domain: string;
  display_name: string | null;
  status: StoreStatus;
  scopes: string[] | null;
  api_version: string | null;
  settings: Record<string, unknown> | null;
  settings_version: number | null;
  settings_updated: Record<string, unknown> | null;
  plugin_state: Record<string, unknown> | null;
  sync_state: Record<string, unknown> | null;
  last_synced_at: string | null;
  connected_by: string | null;
  connected_at: string | null;
  disconnected_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Colonne lette da useCommerceStores (select esplicita, niente '*'). */
export const STORE_COLUMNS =
  'id, organization_id, provider, shop_domain, display_name, status, scopes, api_version, settings, settings_version, ' +
  'settings_updated, plugin_state, sync_state, last_synced_at, connected_by, connected_at, disconnected_at, created_at, updated_at';

/** Riga di get_commerce_store_labels() (15 D2): basta inventory.view. */
export interface StoreLabel {
  id: string;
  provider: CommerceProvider;
  display_name: string | null;
  shop_domain: string;
  status: StoreStatus;
}

/** Risposta di GET commerce/connect-requests/:id (mai client id o secret). */
export interface ConnectRequestView {
  id: string;
  provider: CommerceProvider;
  shop_domain: string;
  status: ConnectRequestStatus;
  install_link: string | null;
  store_id: string | null;
  last_error_code: string | null;
  mode: 'normal' | 'shadow';
}

/** Lavoro del motore (product_sync_jobs con commerce_store_id non nullo). */
export interface CommerceJobRow {
  id: string;
  commerce_store_id: string | null;
  job_kind: JobKind | null;
  status: JobStatus;
  triggered_by: string | null;
  triggered_by_user_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  checkpoint: Record<string, unknown> | null;
  next_attempt_at: string | null;
  error_count: number | null;
  metadata: Record<string, unknown> | null;
  /** Ultimi errori delle fette (max 20, { at, message }): perche' il lavoro si e' interrotto. */
  errors?: unknown;
}

export const JOB_COLUMNS =
  'id, commerce_store_id, job_kind, status, triggered_by, triggered_by_user_id, started_at, completed_at, ' +
  'created_at, updated_at, checkpoint, next_attempt_at, error_count, metadata, errors';

/** Riga di commerce_events (registro). details puo' contenere dati personali: solo con settings_ecommerce.view. */
export interface CommerceEventRow {
  id: string;
  store_id: string | null;
  kind: EventKind;
  topic: string | null;
  entity_type: string | null;
  external_id: string | null;
  status: string;
  message: string | null;
  details: Record<string, unknown> | null;
  actor_id: string | null;
  attempts: number;
  created_at: string;
}

export const EVENT_COLUMNS =
  'id, store_id, kind, topic, entity_type, external_id, status, message, details, actor_id, attempts, created_at';

/** Riga di commerce_change_log (capitolo 14). */
export interface ChangeLogRow {
  id: string;
  store_id: string;
  entity_type: string;
  entity_id: string | null;
  external_id: string | null;
  field_group: string;
  direction: 'to_store' | 'to_flusia';
  before: unknown;
  after: unknown;
  origin: string;
  actor_source: string | null;
  job_id: string | null;
  actor_id: string | null;
  created_at: string;
}

export const CHANGE_COLUMNS =
  'id, store_id, entity_type, entity_id, external_id, field_group, direction, before, after, origin, actor_source, job_id, actor_id, created_at';

/** Riga di commerce_snapshots (fase 1: solo impostazioni e abbinamenti). */
export interface SnapshotRow {
  id: string;
  store_id: string;
  kind: 'nightly' | 'pre_job' | 'pre_settings' | 'manual' | 'pre_migration';
  name: string | null;
  status: 'creating' | 'ready' | 'failed' | 'expired';
  counts: Record<string, unknown> | null;
  job_id: string | null;
  created_by: string | null;
  taken_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export const SNAPSHOT_COLUMNS = 'id, store_id, kind, name, status, counts, job_id, created_by, taken_at, expires_at, created_at';

export const isProvider = (v: unknown): v is CommerceProvider =>
  typeof v === 'string' && (COMMERCE_PROVIDERS as readonly string[]).includes(v);

export const isStoreStatus = (v: unknown): v is StoreStatus =>
  typeof v === 'string' && (STORE_STATUSES as readonly string[]).includes(v);
